import { REACH, reachIndex } from '../lib/reach'

/**
 * The song at the centre, its reach as concentric rings around it.
 *
 * Three deliberate departures from the Overflow prototype this is descended
 * from:
 *
 * 1. The rings are a DIAL, not four switches. Tapping a ring opens the song out
 *    to that ring and everything inside comes with it, which is what the
 *    picture already claims — a wider circle contains the narrower ones. The
 *    prototype let each ring toggle alone, so the drawing could say "open to
 *    the Collective, closed to your Inner Circle", which is not a thing anyone
 *    means.
 *
 * 2. Labels are horizontal, sitting at the top of their own band, rather than
 *    bent around the arc. Curved type is unreadable at 10px, collides with the
 *    stroke, and cannot be translated without re-solving the geometry.
 *
 * 3. Open rings are a translucent stroke rather than solid gold. A disc of pure
 *    accent is the loudest thing that can be put on a screen, and it flattens
 *    the one distinction that matters here: which ring is the EDGE of the
 *    reach. Closed rings are dashed — the same "open door not yet walked
 *    through" language the .thread element uses elsewhere.
 *
 * Every band is stroke-only with no fill, so each circle's hit area is exactly
 * its own annulus and the rings never swallow each other's clicks.
 *
 * This is a picture, not the control surface: it is aria-hidden, and the
 * labelled list of buttons beneath it drives the same state for keyboard and
 * screen reader users. Bolting ARIA onto SVG shapes would be a worse copy of a
 * control that already exists two inches lower.
 */

const R_CORE = 46
const BAND = 26
const SIZE = 320
const C = SIZE / 2

// REACH[0] is 'private' — that's the centre, not a ring. Rings are the four
// tiers outward from it.
const RINGS = REACH.slice(1)

const outerR = (i) => R_CORE + BAND * (i + 1)
const midR = (i) => outerR(i) - BAND / 2

export default function ReachDial({ reach, onChange, title, counts = {} }) {
  const level = reachIndex(reach)
  const info = REACH[level]

  return (
    <div className="dial">
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="dial-svg" aria-hidden="true">
        {RINGS.map((r, i) => {
          const open = i < level
          const edge = i === level - 1
          return (
            <g key={r.id}>
              <circle
                className={`dial-band${open ? ' open' : ''}${edge ? ' edge' : ''}`}
                cx={C} cy={C} r={midR(i)} strokeWidth={BAND}
                onClick={() => onChange(r.id)}
              />
              {/* The hairline on the band's outer boundary. Without it a closed
                  ring has nothing to draw at all. Not clickable — the band
                  underneath already is. */}
              <circle className={`dial-rim${open ? ' open' : ''}`}
                      cx={C} cy={C} r={outerR(i)} />
            </g>
          )
        })}

        <circle className="dial-core" cx={C} cy={C} r={R_CORE}
                onClick={() => onChange('private')} />

        {RINGS.map((r, i) => (
          <text key={`t${r.id}`}
                className={`dial-label${i < level ? ' open' : ''}${i === level - 1 ? ' edge' : ''}`}
                x={C} y={C - midR(i)} dominantBaseline="middle" textAnchor="middle">
            {r.label}
          </text>
        ))}
      </svg>

      <div className="dial-centre" aria-hidden="true">
        <span className="dial-title">{title || 'Your song'}</span>
        <span className="dial-count">
          {reach === 'private' ? 'only you' : counts[reach] || info.label.toLowerCase()}
        </span>
      </div>
    </div>
  )
}
